import { LitElement, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
   StatusVariant,
   Sizes,
   AtlProgressStepItemProps,
} from './progress-stepper.props';
import { enumAttributeConverter } from '../enum-types';
import { IconEnums } from '../icon';

import * as TagNames from './progress-stepper.tagnames';
import { AtlAriaIconProps } from '../icon/icon.props';
import { classMap } from 'lit/directives/class-map.js';
import { AtlStepperClickEvent } from './progress-stepper.events';

@customElement(TagNames.ProgressStepperItem)
export class AtlProgressStepperItem
   extends LitElement
   implements AtlProgressStepItemProps
{
   declare stepId?: string;
   declare stepLabel?: string;
   declare iconStep?: boolean;
   declare variant?: StatusVariant;

   declare stepTitle?: string;
   declare stepSubTitle?: string;

   declare showStatus?: boolean;
   declare showStatusIcon?: boolean;
   declare statusIconSize?: Sizes;

   declare showStroke?: boolean;
   declare active?: boolean;
   declare activeStroke?: boolean;

   declare clickable?: boolean;

   static properties = {
      stepId: { attribute: 'step-id', type: String },
      stepLabel: { attribute: 'step-label', type: String },
      stepTitle: { attribute: 'step-title', type: String },
      stepSubTitle: { attribute: 'step-subtitle', type: String },
      iconStep: { attribute: 'icon-step', type: Boolean },
      variant: {
         type: String,
         converter: enumAttributeConverter(StatusVariant),
      },
      showStroke: { attribute: 'show-stroke', type: Boolean },
      active: { type: Boolean },
      activeStroke: { attribute: 'active-stroke', type: Boolean },
      showStatus: { attribute: 'show-status', type: Boolean },
      showStatusIcon: { attribute: 'show-status-icon', type: Boolean },
      statusIconSize: {
         attribute: 'status-size',
         type: String,
         converter: enumAttributeConverter(Sizes),
      },
      clickable: { type: Boolean },
   };

   private get _iconName() {
      switch (this.variant) {
         case StatusVariant.Overdue:
            return 'circle-exclamation';
         case StatusVariant.DueBy:
            return 'clock';
         case StatusVariant.Skipped:
            return 'ban';
         case StatusVariant.InProgress:
            return 'spinner';
         case StatusVariant.Done:
         default:
            return 'check';
      }
   }

   protected override createRenderRoot() {
      return this;
   }

   private _renderHeaderContent = () => {
      if (this.iconStep) {
         return html`
            <div
               class=${classMap({
                  'progress-step-header': this.clickable ?? true,
               })}
               @click=${() => this.clickable && this._handleClick()}
            >
               <atlantis-icon
                  name=${this._iconName}
                  .iconAriaSettings=${{ ariaHidden: true } as AtlAriaIconProps}
                  iconSource=${IconEnums.IconSource.FontAwesome}
                  iconClassName="icon"
               >
               </atlantis-icon>
            </div>
         `;
      }
      return html` <div
         class=${classMap({ 'progress-step-header': this.clickable ?? true })}
         @click=${() => this.clickable && this._handleClick()}
      >
         ${this.stepLabel}
      </div>`;
   };

   render() {
      return html`
         <div class="step-header">${this._renderHeaderContent()}</div>
         <div class="step-body">
            ${this.stepTitle
               ? html`<span class="step-title" title=${this.stepTitle}
                    >${this.stepTitle}</span
                 >`
               : nothing}
            ${this.stepSubTitle
               ? html`<span class="step-subtitle" title=${this.stepSubTitle}
                    >${this.stepSubTitle}</span
                 >`
               : nothing}
            ${this.showStatus
               ? html`<atlantis-status-chip
                    variant=${this.variant ?? StatusVariant.Done}
                    reverse-order
                    ?show-avatar-icon=${this.showStatusIcon}
                    size=${this.statusIconSize ?? Sizes.Small}
                 ></atlantis-status-chip>`
               : nothing}
         </div>
      `;
   }

   private _handleClick() {
      this.dispatchEvent(
         new AtlStepperClickEvent({ payload: this.stepId ?? '' })
      );
   }
}
